import type { TenantAdminOutboundQuestionnaireQuestionV1 } from "./tenant-admin-outbound-calls-contract.js";
import type { WorkspaceChangedScopeV1 } from "./workspace-refresh-contract.js";

export const clinicianWorkbenchContractVersion = "clinician-workbench-v1" as const;

export type ClinicianWorkbenchContractVersion =
  typeof clinicianWorkbenchContractVersion;

export const clinicianWorkbenchHttpRoutePath =
  "/api/v1/clinician-workbench" as const;

export const clinicianWorkbenchActionContractVersion =
  "clinician-workbench-action-v1" as const;

export type ClinicianWorkbenchActionContractVersion =
  typeof clinicianWorkbenchActionContractVersion;

export const clinicianWorkbenchActionHttpRoutePath =
  "/api/v1/clinician-workbench/actions" as const;

export const clinicianWorkbenchDocumentUploadIntentContractVersion =
  "clinician-workbench-document-upload-intent-v1" as const;

export const clinicianWorkbenchDocumentUploadIntentHttpRoutePath =
  "/api/v1/clinician-workbench/documents/upload-intent" as const;

export const clinicianWorkbenchDocumentCompleteUploadContractVersion =
  "clinician-workbench-document-complete-upload-v1" as const;

export const clinicianWorkbenchDocumentCompleteUploadHttpRoutePath =
  "/api/v1/clinician-workbench/documents/complete-upload" as const;

export const clinicianWorkbenchDocumentStatusContractVersion =
  "clinician-workbench-document-status-v1" as const;

export const clinicianWorkbenchDocumentStatusHttpRoutePathPattern =
  "/api/v1/clinician-workbench/documents/{documentId}/status" as const;

export const clinicianWorkbenchDocumentDownloadContractVersion =
  "clinician-workbench-document-download-v1" as const;

export const clinicianWorkbenchDocumentDownloadHttpRoutePath =
  "/api/v1/clinician-workbench/documents/download" as const;

export const clinicianWorkbenchOutboundCallsContractVersion =
  "clinician-workbench-outbound-calls-v1" as const;

export type ClinicianWorkbenchOutboundCallsContractVersion =
  typeof clinicianWorkbenchOutboundCallsContractVersion;

export const clinicianWorkbenchOutboundCallsHttpRoutePath =
  "/api/v1/clinician-workbench/outbound-calls" as const;

export const clinicianWorkbenchOutboundCallsCommandContractVersion =
  "clinician-workbench-outbound-calls-command-v1" as const;

export type ClinicianWorkbenchOutboundCallsCommandContractVersion =
  typeof clinicianWorkbenchOutboundCallsCommandContractVersion;

export const clinicianWorkbenchOutboundCallsCommandHttpRoutePath =
  "/api/v1/clinician-workbench/outbound-calls/commands" as const;

export const clinicianWorkbenchOutboundCallsAnswersPdfDownloadContractVersion =
  "clinician-workbench-outbound-calls-answers-pdf-download-v1" as const;

export const clinicianWorkbenchOutboundCallsAnswersPdfDownloadHttpRoutePath =
  "/api/v1/clinician-workbench/outbound-calls/answers.pdf" as const;

export const clinicianWorkbenchListScopes = ["my", "all"] as const;

export type ClinicianWorkbenchListScope =
  (typeof clinicianWorkbenchListScopes)[number];

export const clinicianWorkbenchDetailTabs = [
  "information",
  "case-summary",
  "appointments",
  "procedures",
  "medications",
  "documents",
  "phone-calls",
] as const;

export type ClinicianWorkbenchDetailTab =
  (typeof clinicianWorkbenchDetailTabs)[number];

export const clinicianWorkbenchDerivedFlagKeys = [
  "pre-op",
  "post-op",
  "imaging-active",
  "pre-admission",
  "chronic-follow-up",
] as const;

export type ClinicianWorkbenchDerivedFlagKey =
  (typeof clinicianWorkbenchDerivedFlagKeys)[number];

export const clinicianWorkbenchMessageSenderKinds = [
  "patient",
  "clinician",
  "system",
] as const;

export type ClinicianWorkbenchMessageSenderKind =
  (typeof clinicianWorkbenchMessageSenderKinds)[number];

export const clinicianWorkbenchMessageDeliveryStates = [
  "sent",
  "delivered",
  "system",
] as const;

export type ClinicianWorkbenchMessageDeliveryState =
  (typeof clinicianWorkbenchMessageDeliveryStates)[number];

export const clinicianWorkbenchVoiceTranscriptSpeakerKinds = [
  "patient",
  "system",
] as const;

export type ClinicianWorkbenchVoiceTranscriptSpeakerKind =
  (typeof clinicianWorkbenchVoiceTranscriptSpeakerKinds)[number];

export const clinicianWorkbenchMessageReviewStates = [
  "none",
  "pending-approval",
  "approved",
  "approved-edited",
  "rejected",
] as const;

export type ClinicianWorkbenchMessageReviewState =
  (typeof clinicianWorkbenchMessageReviewStates)[number];

export const clinicianWorkbenchAssignmentTargetKinds = [
  "clinician",
  "team",
  "queue",
] as const;

export type ClinicianWorkbenchAssignmentTargetKind =
  (typeof clinicianWorkbenchAssignmentTargetKinds)[number];

export const clinicianWorkbenchDocumentAvailabilityStates = [
  "download-ready",
  "metadata-only",
] as const;

export type ClinicianWorkbenchDocumentAvailabilityState =
  (typeof clinicianWorkbenchDocumentAvailabilityStates)[number];

export const clinicianWorkbenchClinicalNoteTargetKinds = [
  "patient",
  "appointment",
  "procedure",
  "medication",
  "document",
] as const;

export type ClinicianWorkbenchClinicalNoteTargetKind =
  (typeof clinicianWorkbenchClinicalNoteTargetKinds)[number];

export const clinicianWorkbenchClinicalNoteVisibilityStates = [
  "clinical",
  "patient-visible",
] as const;

export type ClinicianWorkbenchClinicalNoteVisibilityState =
  (typeof clinicianWorkbenchClinicalNoteVisibilityStates)[number];

export const clinicianWorkbenchClinicalNoteStatuses = [
  "active",
  "archived",
] as const;

export type ClinicianWorkbenchClinicalNoteStatus =
  (typeof clinicianWorkbenchClinicalNoteStatuses)[number];

export const clinicianWorkbenchClinicalRecordKinds = [
  "appointment",
  "procedure",
  "medication",
  "document",
] as const;

export type ClinicianWorkbenchClinicalRecordKind =
  (typeof clinicianWorkbenchClinicalRecordKinds)[number];

export const clinicianWorkbenchClinicalRecordStatuses = [
  "active",
  "archived",
] as const;

export type ClinicianWorkbenchClinicalRecordStatus =
  (typeof clinicianWorkbenchClinicalRecordStatuses)[number];

export const clinicianWorkbenchActionKinds = [
  "add-patient-to-my-list",
  "complete-task-assignment",
  "mark-document-reviewed",
  "send-reply",
  "approve-message-review",
  "reject-message-review",
  "refresh-case-summary",
  "clear-patient-attention",
  "upsert-clinical-record",
  "archive-clinical-record",
  "reactivate-clinical-record",
  "upsert-clinical-note",
  "archive-clinical-note",
  "reactivate-clinical-note",
  "upsert-patient-record",
  "mark-red-flags-reviewed",
] as const;

export type ClinicianWorkbenchActionKind =
  (typeof clinicianWorkbenchActionKinds)[number];

export const clinicianWorkbenchActionStatuses = ["applied"] as const;

export type ClinicianWorkbenchActionStatus =
  (typeof clinicianWorkbenchActionStatuses)[number];

export interface ClinicianWorkbenchRequestV1 {
  schemaVersion: ClinicianWorkbenchContractVersion;
  tenantId: string;
  siteId: string | null;
  clinicianUserId: string;
  listScope: ClinicianWorkbenchListScope;
  attentionOnly: boolean;
  searchQuery: string | null;
  selectedPatientId: string | null;
  detailTab: ClinicianWorkbenchDetailTab;
}

export interface ClinicianWorkbenchHttpQuery {
  tenantId: string;
  siteId?: string | null;
  clinicianUserId: string;
  listScope?: string | null;
  attentionOnly?: string | null;
  searchQuery?: string | null;
  selectedPatientId?: string | null;
  detailTab?: string | null;
}

export interface ClinicianWorkbenchMetricItem {
  id: string;
  label: string;
  valueText: string;
}

export interface ClinicianWorkbenchMetricGroup {
  id: string;
  title: string;
  items: ClinicianWorkbenchMetricItem[];
}

export interface ClinicianWorkbenchPatientListItem {
  patientId: string;
  displayName: string;
  identifier: string;
  summaryText: string;
  statusLabel: string;
  lastActivityAt: string | null;
  /**
   * Date of surgery (ISO date) driving the workbench surgery-date list view.
   * Optional until the hosted projections emit it; the scaffold transport
   * derives it from the patient record fixture.
   */
  dateOfService?: string | null;
  /**
   * Count of red-flag questionnaire answers awaiting clinician review.
   * Optional until the hosted projections track review state; the scaffold
   * transport derives it from the outbound call fixtures.
   */
  pendingRedFlagCount?: number;
  derivedFlagKeys: ClinicianWorkbenchDerivedFlagKey[];
  requiresAttention: boolean;
  inMyPatients: boolean;
  unreadMessageCount: number;
  pendingDocumentCount: number;
  openOutboundCallCount: number;
  hasVoiceCallHandoff: boolean;
  materialChangeToken: string;
}

export interface ClinicianWorkbenchConversationMessage {
  messageId: string;
  senderKind: ClinicianWorkbenchMessageSenderKind;
  senderDisplayName: string;
  bodyText: string;
  sentAt: string;
  deliveryState: ClinicianWorkbenchMessageDeliveryState;
  review: ClinicianWorkbenchMessageReview | null;
}

export interface ClinicianWorkbenchConversation {
  conversationId: string;
  messages: ClinicianWorkbenchConversationMessage[];
}

export interface ClinicianWorkbenchVoiceTranscriptTurn {
  id: string;
  speakerKind: ClinicianWorkbenchVoiceTranscriptSpeakerKind;
  speakerLabel: string;
  bodyText: string;
  sentAt: string;
}

export interface ClinicianWorkbenchVoiceCallSummary {
  callId: string;
  connectContactId: string | null;
  receivedAt: string;
  previewText: string;
  sourceLabel: string | null;
  turnCount: number;
  hasClinicianHandoff: boolean;
  questionClass: string | null;
  transcriptTurns: ClinicianWorkbenchVoiceTranscriptTurn[];
}

export interface ClinicianWorkbenchMessageReview {
  state: ClinicianWorkbenchMessageReviewState;
  confidencePercent: number | null;
  criticalityPercent: number | null;
  clinicianNotes: string | null;
  canApprove: boolean;
  canReject: boolean;
  canEdit: boolean;
}

export interface ClinicianWorkbenchDetailRow {
  id: string;
  label: string;
  valueText: string;
  clinicalStatus?: ClinicianWorkbenchClinicalRecordStatus;
  notes?: ClinicianWorkbenchClinicalNote[];
}

export interface ClinicianWorkbenchDetailSection {
  id: ClinicianWorkbenchDetailTab;
  title: string;
  summaryText: string | null;
  rows: ClinicianWorkbenchDetailRow[];
  notes?: ClinicianWorkbenchClinicalNote[];
}

export interface ClinicianWorkbenchTaskAssignmentSummary {
  assignmentId: string;
  taskLabel: string;
  targetKind: ClinicianWorkbenchAssignmentTargetKind;
  targetLabel: string;
  statusLabel: string;
  canComplete: boolean;
}

export interface ClinicianWorkbenchApprovalSummary {
  approvalId: string;
  label: string;
  ownerLabel: string;
  statusLabel: string;
  updatedAtText: string;
}

export interface ClinicianWorkbenchDeliverySummary {
  deliveryId: string;
  channelLabel: string;
  recipientLabel: string;
  statusLabel: string;
  updatedAtText: string;
}

export interface ClinicianWorkbenchDocumentSummary {
  documentId: string;
  label: string;
  summaryText: string;
  statusLabel: string;
  createdAt: string;
  createdByLabel: string;
  availability: ClinicianWorkbenchDocumentAvailabilityState;
  canMarkReviewed: boolean;
  countsTowardPendingDocumentCount: boolean;
  clinicalStatus?: ClinicianWorkbenchClinicalRecordStatus;
  notes?: ClinicianWorkbenchClinicalNote[];
}

export interface ClinicianWorkbenchDocumentUploadIntentRequestV1 {
  schemaVersion: typeof clinicianWorkbenchDocumentUploadIntentContractVersion;
  workbenchRequest: ClinicianWorkbenchRequestV1;
  patientId: string;
  filename: string;
  mediaType: string;
  byteLength: number;
  sha256?: string | null;
  title?: string | null;
  documentTypeKey?: string | null;
}

export interface ClinicianWorkbenchDocumentUploadIntentResponseV1 {
  schemaVersion: typeof clinicianWorkbenchDocumentUploadIntentContractVersion;
  tenantId: string;
  patientId: string;
  uploadId: string;
  documentId: string;
  uploadMethod: "PUT";
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  objectKey: string;
  expiresAt: string;
  maxByteLength: number;
}

export interface ClinicianWorkbenchDocumentCompleteUploadRequestV1 {
  schemaVersion: typeof clinicianWorkbenchDocumentCompleteUploadContractVersion;
  workbenchRequest: ClinicianWorkbenchRequestV1;
  patientId: string;
  uploadId: string;
  documentId: string;
  byteLength: number;
  sha256?: string | null;
  title?: string | null;
  documentTypeKey?: string | null;
}

export interface ClinicianWorkbenchDocumentIngestionStatusV1 {
  documentId: string;
  uploadId: string | null;
  ingestionStatus: "received" | "extracting" | "ready" | "failed" | "quarantined";
  extractionStatus: "not_needed" | "pending" | "running" | "ready" | "failed";
  extractionMethod: string | null;
  summaryText: string | null;
  failureReason: string | null;
  aiContextEligible: boolean;
  reviewStatus: "not_required" | "pending_review" | "reviewed" | "rejected";
  previewStatus: "pending" | "ready" | "failed";
  downloadReady: boolean;
  originalSha256: string | null;
  extractedAt: string | null;
  updatedAt: string;
}

export interface ClinicianWorkbenchDocumentCompleteUploadResponseV1 {
  schemaVersion: typeof clinicianWorkbenchDocumentCompleteUploadContractVersion;
  tenantId: string;
  patientId: string;
  document: ClinicianWorkbenchDocumentSummary;
  ingestion: ClinicianWorkbenchDocumentIngestionStatusV1;
  workbench: ClinicianWorkbenchResponseV1;
  changedScopes?: WorkspaceChangedScopeV1[];
}

export interface ClinicianWorkbenchDocumentStatusResponseV1 {
  schemaVersion: typeof clinicianWorkbenchDocumentStatusContractVersion;
  tenantId: string;
  patientId: string;
  ingestion: ClinicianWorkbenchDocumentIngestionStatusV1;
}

export const clinicianWorkbenchOutboundCallActions = [
  "queue-resume-now",
  "start-live-call",
  "start-new-round",
  "assign-review",
  "mark-review-reviewed",
  "escalate-review",
  "search-medication-catalog",
  "resolve-medication",
  // Manual continuation of an interrupted AI call: clinician-entered answers
  // mirror into the outbound session (a later phone resume skips them);
  // completing manually settles the session and stops automated dialing.
  "record-manual-answers",
  "complete-session-manually",
] as const;

export type ClinicianWorkbenchOutboundCallAction =
  (typeof clinicianWorkbenchOutboundCallActions)[number];

export type ClinicianWorkbenchOutboundQuestionnaireAnswerEventTypeV1 =
  | "answer_captured"
  | "answer_changed"
  | "answer_redacted"
  | "answer_skipped"
  | "definition_requested"
  | "repeat_requested"
  | "go_back_requested"
  | "change_requested"
  | "skip_requested"
  | "navigation_requested";

export interface ClinicianWorkbenchOutboundQuestionnaireAnswerEventV1 {
  eventKey: string;
  eventType: ClinicianWorkbenchOutboundQuestionnaireAnswerEventTypeV1;
  questionKey: string;
  occurredAt: string | null;
  source: "voice" | "patient" | "system" | "clinician";
  summaryText?: string | null;
  answerSummary?: string | null;
  previousAnswerSummary?: string | null;
  rawAnswerText?: string | null;
  confidenceLevel?: string | null;
}

export interface ClinicianWorkbenchOutboundMedicationSourceV1 {
  kind: string;
  rawText: string | null;
  reference?: string | null;
}

export interface ClinicianWorkbenchOutboundMedicationIdentityMatchV1 {
  enabled: boolean;
  status: string;
  reason?: string | null;
  normalizedDrugCandidate?: string | null;
  externalSource?: string | null;
  matchConfidence?: number | null;
  humanReviewed?: boolean;
}

export interface ClinicianWorkbenchOutboundMedicationEntryV1 {
  name: string | null;
  patientReportedName: string | null;
  dose: number | string | null;
  unit: string | null;
  route: string | null;
  frequency: string | null;
  reason: string | null;
  lastTaken: string | null;
  confidenceLevel: string | null;
  source: ClinicianWorkbenchOutboundMedicationSourceV1;
  identityMatchStatus: string | null;
  matchProvenance?: string | null;
  /** Index into the answer's stored medications array; addresses resolve-medication. */
  entryIndex?: number | null;
  /** RxCUI once matched/resolved against the medication reference catalog. */
  rxcui?: string | null;
  /** True when a clinician must pick the exact drug from the catalog drop-down. */
  needsResolution?: boolean;
  resolvedByLabel?: string | null;
  resolvedAt?: string | null;
}

export interface ClinicianWorkbenchMedicationCatalogStrengthV1 {
  strength: string | null;
  dosageForm: string | null;
  route: string | null;
  rxcui: string | null;
}

export interface ClinicianWorkbenchMedicationCatalogCandidateV1 {
  drugName: string;
  rxcui: string | null;
  strengths: ClinicianWorkbenchMedicationCatalogStrengthV1[];
}

export interface ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1 {
  questionKey: string;
  promptText: string;
  sectionKey?: string | null;
  sectionLabel?: string | null;
  clinicianReviewLabel?: string | null;
  answerStatus: string;
  answerSummary: string;
  rawAnswerText: string | null;
  answeredAt: string | null;
  clinicianReviewRequired: boolean;
  redFlagTriggered: boolean;
  reviewSignal?: "none" | "yellow_flag" | "red_flag";
  reviewSignalLabel?: string | null;
  reviewReasonKeys?: string[];
  reviewReasonLabels?: string[];
  reviewQueueKey?: string | null;
  reviewPriority?: "routine" | "soon" | "urgent" | null;
  reviewStatus?:
    | "not_required"
    | "pending_review"
    | "in_review"
    | "reviewed"
    | "escalated";
  reviewOwnerKind?: "clinician" | "team" | "department" | "queue" | null;
  reviewOwnerReference?: string | null;
  reviewOwnerLabel?: string | null;
  reviewNotes?: string | null;
  changedSinceFirstAnswer?: boolean;
  answerEvents?: ClinicianWorkbenchOutboundQuestionnaireAnswerEventV1[];
  definitionRequestCount?: number;
  definitionRequested?: boolean;
  medicationReconciliationStatus?: string | null;
  medicationEntryCount?: number;
  medicationEntries?: ClinicianWorkbenchOutboundMedicationEntryV1[];
  medicationIdentityMatching?: ClinicianWorkbenchOutboundMedicationIdentityMatchV1;
}

/**
 * Historical answer surfaces must not interpolate today's chart values into a
 * prompt captured by an earlier call. Until resolved prompt text is snapshotted
 * with each answer, use stable review wording for known confirmation questions
 * and visibly neutralize any future template tokens.
 */
export function resolveClinicianQuestionnaireReviewPromptText(
  questionKey: string,
  promptText: string,
): string {
  const stablePrompt =
    questionKey === "confirm_planned_procedure"
      ? "Did the patient confirm the planned procedure?"
      : questionKey === "confirm_scheduled_date"
        ? "What scheduled procedure date did the patient provide or confirm?"
        : questionKey === "confirm_surgeon"
          ? "Did the patient confirm the surgeon?"
          : null;

  if (stablePrompt) {
    return stablePrompt;
  }

  return promptText.replace(
    /\{([A-Za-z][A-Za-z0-9_-]*)\}/gu,
    (_, token: string) =>
      `[${token
        .replace(/([a-z0-9])([A-Z])/gu, "$1 $2")
        .replace(/[_-]+/gu, " ")
        .trim()
        .toLowerCase()} recorded with this answer]`,
  );
}

export interface ClinicianWorkbenchOutboundQuestionnaireCallV1 {
  targetId: string;
  campaignId: string;
  campaignDisplayName: string;
  questionnaireDisplayName: string;
  questionnaireTemplateVersionId: string | null;
  questionnaireVersionNumber: number | null;
  questionnaireVersionLabel: string | null;
  campaignQuestionnaireTemplateVersionId: string | null;
  campaignQuestionnaireVersionNumber: number | null;
  campaignQuestionnaireVersionLabel: string | null;
  questionnaireVersionPinnedToSession: boolean;
  targetStatus: string;
  sessionId: string | null;
  sessionStatus: string | null;
  /** Opaque database revision for concurrency-safe manual answer updates. */
  sessionRevision: string | null;
  currentQuestionKey: string | null;
  answeredQuestionCount: number;
  totalQuestionCount: number;
  redFlagCount: number;
  /**
   * True once a clinician marked this call's red-flag answers reviewed and
   * no red-flag answer landed since. Hosted reads derive it from the
   * session's review record; omitted when the call has no red flags. The
   * "Mark reviewed" affordance renders only when the value is exactly false.
   */
  redFlagsReviewed?: boolean;
  nextEligibleAttemptAt: string | null;
  queuedAttemptId: string | null;
  queuedSessionId: string | null;
  queuedScheduledFor: string | null;
  attemptCount: number;
  lastAttemptAt: string | null;
  lastAttemptOutcome: string | null;
  phoneNumberLast4: string | null;
  patientTimezone: string | null;
  callWindowLabel: string | null;
  outsideCallWindow: boolean;
  repeatWarning: boolean;
  warningMessages: string[];
  canQueueResume: boolean;
  canStartQueuedCall: boolean;
  canStartNewRound: boolean;
  primaryAction: ClinicianWorkbenchOutboundCallAction | null;
  primaryActionLabel: string | null;
  answerPreview: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1[];
  updatedAt: string | null;
}

export interface ClinicianWorkbenchOutboundCallsRequestV1 {
  schemaVersion: ClinicianWorkbenchOutboundCallsContractVersion;
  tenantId: string;
  siteId: string | null;
  clinicianUserId: string;
  patientId: string;
}

export interface ClinicianWorkbenchOutboundCallsResponseV1 {
  schemaVersion: ClinicianWorkbenchOutboundCallsContractVersion;
  tenantId: string;
  patientId: string;
  generatedAt: string;
  items: ClinicianWorkbenchOutboundQuestionnaireCallV1[];
}

export interface ClinicianWorkbenchOutboundCallsCommandRequestV1
  extends Omit<ClinicianWorkbenchOutboundCallsRequestV1, "schemaVersion"> {
  schemaVersion: ClinicianWorkbenchOutboundCallsCommandContractVersion;
  command: {
    action: ClinicianWorkbenchOutboundCallAction;
    targetId: string;
    attemptId?: string | null;
    sessionId?: string | null;
    reviewOwnerKind?: "clinician" | "team" | "department" | "queue" | null;
    reviewOwnerReference?: string | null;
    reviewNotes?: string | null;
    escalationReasonKey?: string | null;
    acknowledgeOutsideWindow?: boolean | null;
    acknowledgeRepeat?: boolean | null;
    reasonText?: string | null;
    /** search-medication-catalog: the clinician's search text. */
    medicationQuery?: string | null;
    /** resolve-medication: which answer and entry is being resolved. */
    medicationQuestionKey?: string | null;
    medicationEntryIndex?: number | null;
    resolvedDrugName?: string | null;
    resolvedStrength?: string | null;
    resolvedRxcui?: string | null;
    /** record-manual-answers / complete-session-manually: clinician-entered
     *  answers (canonical values) to mirror into the outbound session. */
    manualAnswers?: ClinicianWorkbenchOutboundManualAnswerV1[] | null;
    /** Answer keys explicitly cleared by the clinician. These are removed from
     *  resumable engine state while their audit row remains as skipped. */
    clearedQuestionKeys?: string[] | null;
    /** Opaque revision captured with the opened questionnaire. Manual writes
     *  fail if the phone session changed after the clinician opened it. */
    expectedSessionRevision?: string | null;
  };
}

export interface ClinicianWorkbenchOutboundManualAnswerV1 {
  questionKey: string;
  /** Canonical value: boolean for yes_no/confirmation, string or string[]
   *  for choices, string for text/date/number. */
  value: boolean | number | string | string[];
  /** Verbatim text as typed, when it differs from the canonical value. */
  rawText?: string | null;
}

export interface ClinicianWorkbenchOutboundCallsCommandResponseV1 {
  schemaVersion: ClinicianWorkbenchOutboundCallsCommandContractVersion;
  tenantId: string;
  patientId: string;
  summaryText: string;
  targetId: string | null;
  sessionId: string | null;
  attemptId: string | null;
  contactId: string | null;
  generatedAt: string;
  items: ClinicianWorkbenchOutboundQuestionnaireCallV1[];
  /** Present for search-medication-catalog commands. */
  medicationCatalogCandidates?: ClinicianWorkbenchMedicationCatalogCandidateV1[] | null;
}

export const clinicianWorkbenchQuestionnaireDefinitionContractVersion =
  "clinician-workbench-questionnaire-definition-v1" as const;

export type ClinicianWorkbenchQuestionnaireDefinitionContractVersion =
  typeof clinicianWorkbenchQuestionnaireDefinitionContractVersion;

export const clinicianWorkbenchQuestionnaireDefinitionHttpRoutePath =
  "/api/v1/clinician-workbench/questionnaire-definition" as const;

export type ClinicianWorkbenchQuestionnaireVersionStatus =
  | "draft"
  | "pending_review"
  | "approved"
  | "active"
  | "retired";

/** Tenant-configured questionnaire definition served to the workbench: the
 *  template version that drives the manual-entry surface, the status card and
 *  the printed History Form, plus that version's chart bindings. The tenant's
 *  feature flag (clinician-questionnaire-entry) selects the template key and
 *  whether the newest draft or the active version is served, so new clients
 *  are enabled by seeding a template and flipping a flag — no code changes. */
export interface ClinicianWorkbenchQuestionnaireDefinitionV1 {
  templateKey: string;
  displayName: string;
  /** The served version. Present on hosted reads; a manual-entry surface
   *  for a patient whose session is pinned to another version requests that
   *  version explicitly (questionnaireTemplateVersionId) so answers are never
   *  processed against a different definition than they were given on. */
  versionId?: string | null;
  versionNumber?: number | null;
  versionLabel: string | null;
  versionStatus: ClinicianWorkbenchQuestionnaireVersionStatus;
  estimatedMinutes: number | null;
  questions: TenantAdminOutboundQuestionnaireQuestionV1[];
  chartBindings: ClinicianWorkbenchQuestionnaireFieldBinding[];
}

export interface ClinicianWorkbenchQuestionnaireDefinitionResponseV1 {
  schemaVersion: ClinicianWorkbenchQuestionnaireDefinitionContractVersion;
  tenantId: string;
  generatedAt: string;
  definition: ClinicianWorkbenchQuestionnaireDefinitionV1 | null;
}

export interface ClinicianWorkbenchClinicalNote {
  noteId: string;
  targetKind: ClinicianWorkbenchClinicalNoteTargetKind;
  targetId: string | null;
  status: ClinicianWorkbenchClinicalNoteStatus;
  title: string;
  bodyText: string;
  authoredAt: string | null;
  authoredByLabel: string;
  visibility: ClinicianWorkbenchClinicalNoteVisibilityState;
  sourceLabel: string | null;
}

export const clinicianWorkbenchPatientRecordSources = [
  "manual",
  "questionnaire",
  "hst_import",
  "passport_import",
  "legacy_migration",
] as const;

export type ClinicianWorkbenchPatientRecordSource =
  (typeof clinicianWorkbenchPatientRecordSources)[number];

/** The only provenances a workbench client may declare on an upsert. The
 *  import sources are deliberately absent: they say a system of record
 *  produced the value, they are what the questionnaire's clear rule refuses
 *  to touch, and a client that could claim one would make its own writes
 *  permanently un-clearable while misrepresenting the audit trail. Anything
 *  outside this list is rejected at the HTTP boundary rather than defaulted,
 *  so a client asking for a provenance it may not have is told so. */
export const clientDeclarablePatientRecordWriterSources = [
  "manual",
  "questionnaire",
] as const satisfies readonly ClinicianWorkbenchPatientRecordSource[];

export function isClientDeclarablePatientRecordWriterSource(
  value: unknown,
): value is (typeof clientDeclarablePatientRecordWriterSources)[number] {
  return (
    typeof value === "string" &&
    (clientDeclarablePatientRecordWriterSources as readonly string[]).includes(
      value,
    )
  );
}

export type ClinicianWorkbenchPatientRecordReviewState =
  | "suggested"
  | "confirmed";

export interface ClinicianWorkbenchPatientRecordFieldMeta {
  source: ClinicianWorkbenchPatientRecordSource;
  reviewState?: ClinicianWorkbenchPatientRecordReviewState;
  updatedAt: string;
  updatedBy: string;
}

export interface ClinicianWorkbenchPatientRecordPhone {
  number: string | null;
  okayToLeaveVoicemail: boolean | null;
  weekdayNumber: boolean | null;
}

export interface ClinicianWorkbenchPatientRecordContact {
  name: string | null;
  relationship: string | null;
  phone: string | null;
}

export interface ClinicianWorkbenchPatientRecordPhysician {
  name: string | null;
  phone: string | null;
  specialty: string | null;
}

export type ClinicianWorkbenchPatientRecordSex = "male" | "female";

export const clinicianWorkbenchSurgeryTypeCategories = [
  "lower-extremity-arthroscopy",
  "lower-extremity-arthroplasty",
  "leg-fracture-surgery",
  "upper-extremity-arthroscopy",
  "upper-extremity-arthroplasty",
  "upper-extremity-fracture-surgery",
  "laparoscopy",
  "central-venous-access",
  "great-veins-of-leg",
  "other",
] as const;

export type ClinicianWorkbenchSurgeryTypeCategory =
  (typeof clinicianWorkbenchSurgeryTypeCategories)[number];

export interface ClinicianWorkbenchPatientRecordPatientSection {
  firstName: string | null;
  lastName: string | null;
  mrn: string | null;
  dateOfBirth: string | null;
  sex: ClinicianWorkbenchPatientRecordSex | null;
  addressStreet: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
  heightInches: number | null;
  weightPounds: number | null;
  primaryPhone: ClinicianWorkbenchPatientRecordPhone;
  secondaryPhone: ClinicianWorkbenchPatientRecordPhone;
  workPhone: ClinicianWorkbenchPatientRecordPhone;
  homePhone: ClinicianWorkbenchPatientRecordPhone;
  email: string | null;
  primaryLanguage: string | null;
  needsTranslator: boolean | null;
  maritalStatus: string | null;
  race: string | null;
  ethnicity: string | null;
  religiousAffiliation: string | null;
  legalGuardian: boolean | null;
  advanceDirective: string | null;
}

export interface ClinicianWorkbenchPatientRecordCaseSection {
  dateOfService: string | null;
  caseTime: string | null;
  surgeonName: string | null;
  procedures: string[];
  preopDiagnosis: string | null;
  surgeryDurationMinutes: number | null;
  surgeryTypeCategory: ClinicianWorkbenchSurgeryTypeCategory | null;
}

export interface ClinicianWorkbenchPatientRecordContactsSection {
  emergencyContact1: ClinicianWorkbenchPatientRecordContact;
  emergencyContact2: ClinicianWorkbenchPatientRecordContact;
  rideHome: ClinicianWorkbenchPatientRecordContact;
  caregiver: ClinicianWorkbenchPatientRecordContact;
  primaryCarePhysician: ClinicianWorkbenchPatientRecordPhysician;
  specialist1: ClinicianWorkbenchPatientRecordPhysician;
  specialist2: ClinicianWorkbenchPatientRecordPhysician;
  specialist3: ClinicianWorkbenchPatientRecordPhysician;
  mayContactPhysicians: boolean | null;
  preferredPharmacy: { name: string | null; phone: string | null };
}

export interface ClinicianWorkbenchPatientRecordPayer {
  companyName: string | null;
  companyPhone: string | null;
  companyAddress: string | null;
  policyNumber: string | null;
  groupNumber: string | null;
  policyHolderName: string | null;
  policyHolderRelationship: string | null;
  policyHolderAddress: string | null;
  policyHolderPhone: string | null;
  employerName: string | null;
  employerPhone: string | null;
  employerAddress: string | null;
}

export type ClinicianWorkbenchSecondaryPayerStatus =
  | "unknown"
  | "none"
  | "present";

export interface ClinicianWorkbenchPatientRecordBenefits {
  deductibleCents: number | null;
  deductibleMetCents: number | null;
  outOfPocketMaxCents: number | null;
  outOfPocketMetCents: number | null;
  coInsurancePercent: number | null;
  coPaymentCents: number | null;
  patientResponsibilityCents: number | null;
}

export interface ClinicianWorkbenchPatientRecordInsuranceSection {
  primaryPayer: ClinicianWorkbenchPatientRecordPayer;
  secondaryPayerStatus: ClinicianWorkbenchSecondaryPayerStatus;
  secondaryPayer: ClinicianWorkbenchPatientRecordPayer;
  benefits: ClinicianWorkbenchPatientRecordBenefits;
}

export interface ClinicianWorkbenchPatientRecordMedication {
  name: string;
  dose: string | null;
  frequency: string | null;
  reason: string | null;
}

export const clinicianWorkbenchAllergyCategories = [
  "medication",
  "latex",
  "adhesive-tape",
  "iv-contrast",
  "environmental",
  "food",
  "other",
] as const;

export type ClinicianWorkbenchAllergyCategory =
  (typeof clinicianWorkbenchAllergyCategories)[number];

export interface ClinicianWorkbenchPatientRecordAllergy {
  category: ClinicianWorkbenchAllergyCategory;
  substance: string;
  reaction: string | null;
}

export interface ClinicianWorkbenchPatientRecordPastSurgery {
  surgery: string;
  approximateDate: string | null;
  problems: string | null;
}

export interface ClinicianWorkbenchPatientRecordAnesthesiaHistory {
  malignantHyperthermia: boolean | null;
  malignantHyperthermiaWho: string | null;
  pseudocholinesteraseDeficiency: boolean | null;
  postoperativeNauseaVomiting: boolean | null;
  motionSickness: boolean | null;
  otherDescription: string | null;
  priorComplications: boolean | null;
}

export const clinicianWorkbenchBasicHistoryKeys = [
  "smoking",
  "otherTobacco",
  "alcohol",
  "streetDrugs",
  "mobilityProblems",
  "glasses",
  "contactLenses",
  "blind",
  "hearingAids",
  "bodyPiercings",
  "dentalWork",
  "artificialJoints",
  "metalImplants",
  "pacemaker",
  "aicd",
  "cardiacStent",
  "nerveStimulator",
  "dizzinessVertigo",
  "visualImpairment",
  "confusion",
  "alteredElimination",
  "historyOfFalls",
  "medsCausingDizziness",
] as const;

export type ClinicianWorkbenchBasicHistoryKey =
  (typeof clinicianWorkbenchBasicHistoryKeys)[number];

export interface ClinicianWorkbenchPatientRecordHistoryItem {
  answer: boolean | null;
  detail: string | null;
}

export const clinicianWorkbenchReviewOfSystemsKeys = [
  "cardiovascular",
  "pulmonary",
  "renal",
  "hepatic",
  "neurological",
  "gastrointestinal",
  "endocrine",
  "oncologic",
  "infectiousDiseases",
  "hematological",
  "musculoskeletal",
  "psychiatric",
  "obgyn",
  "other",
] as const;

export type ClinicianWorkbenchReviewOfSystemsKey =
  (typeof clinicianWorkbenchReviewOfSystemsKeys)[number];

export interface ClinicianWorkbenchPatientRecordSystemFinding {
  code: string;
  label: string;
}

export interface ClinicianWorkbenchPatientRecordSystemReview {
  findings: ClinicianWorkbenchPatientRecordSystemFinding[];
  detail: string | null;
}

export const clinicianWorkbenchDvtRiskFactorKeys = [
  "bloodClottingDisorders",
  "cancer",
  "currentlyPregnant",
  "postPartumWithin30Days",
  "miscarriageWithin30Days",
  "oralContraceptives",
  "hormoneReplacementTherapy",
  "twoOrMoreMiscarriages",
  "surgeryWithin30Days",
  "varicoseVeins",
  "colitisOrCrohns",
  "longTripPlanned",
  "centralVenousCatheter",
  "immobilizingCastWithin30Days",
  "bedConfined72Hours",
] as const;

export type ClinicianWorkbenchDvtRiskFactorKey =
  (typeof clinicianWorkbenchDvtRiskFactorKeys)[number];

/**
 * Typed homes for the conditional follow-up answers the questionnaire
 * captures behind positive gate questions (spec §4.3 / workbook
 * record_field_path column). Every leaf is nullable — absent means the
 * gate was negative or the detail was not captured.
 */
export interface ClinicianWorkbenchClinicalStructuredDetails {
  pacemaker: { manufacturer: string | null };
  cardiacStent: {
    yearPlaced: string | null;
    drugEluting: boolean | null;
    stentType: string | null;
  };
  pulmonaryEmbolism: {
    hasHistory: boolean | null;
    eventDate: string | null;
    treatment: string | null;
    recurrence: boolean | null;
    onAnticoagulation: boolean | null;
  };
  homeOxygen: {
    flowParameters: string | null;
    usagePattern: "continuous" | "intermittent" | null;
    portableAvailable: boolean | null;
  };
  cancer: {
    diagnosisDate: string | null;
    lastTreatmentDate: string | null;
    treatmentTypes: string[];
    currentStatus: "in treatment" | "remission" | "monitored" | null;
  };
  cardiacEvent: {
    hospitalized: boolean | null;
    treatment: string | null;
    lastEventDate: string | null;
    unresolved: boolean | null;
  };
  openWound: {
    present: boolean | null;
    nearSurgicalSite: boolean | null;
    woundType: string | null;
    infectionSigns: boolean | null;
    treatment: string | null;
  };
  currentIllness: {
    feverPresent: boolean | null;
    onsetDate: string | null;
    treatment: string | null;
    improving: boolean | null;
    surgeonNotified: boolean | null;
  };
}

export interface ClinicianWorkbenchPatientRecordClinicalSection {
  noKnownMedications: boolean | null;
  medications: ClinicianWorkbenchPatientRecordMedication[];
  noKnownAllergies: boolean | null;
  allergies: ClinicianWorkbenchPatientRecordAllergy[];
  noSurgicalHistory: boolean | null;
  surgicalHistory: ClinicianWorkbenchPatientRecordPastSurgery[];
  anesthesiaHistory: ClinicianWorkbenchPatientRecordAnesthesiaHistory;
  structuredDetails: ClinicianWorkbenchClinicalStructuredDetails;
  neckCircumferenceOver15_75: "yes" | "no" | "not_sure" | null;
  /**
   * Catch-all for questionnaire answers with no typed record home yet
   * (keyed by questionKey). Guarantees the no-orphan-answers rule: every
   * captured answer is persisted and printable even before a structured
   * field exists for it. Values are the canonical answer only.
   */
  questionnaireAnswers: Record<string, string | boolean | number | string[]>;
  basicHistory: Record<
    ClinicianWorkbenchBasicHistoryKey,
    ClinicianWorkbenchPatientRecordHistoryItem
  >;
  reviewOfSystems: Record<
    ClinicianWorkbenchReviewOfSystemsKey,
    ClinicianWorkbenchPatientRecordSystemReview
  >;
  dvtRiskFactors: Record<ClinicianWorkbenchDvtRiskFactorKey, boolean | null>;
}

export type ClinicianWorkbenchPatientRecordFieldPath = string;

export interface ClinicianWorkbenchPatientRecordV1 {
  patient: ClinicianWorkbenchPatientRecordPatientSection;
  case: ClinicianWorkbenchPatientRecordCaseSection;
  contacts: ClinicianWorkbenchPatientRecordContactsSection;
  insurance: ClinicianWorkbenchPatientRecordInsuranceSection;
  clinical: ClinicianWorkbenchPatientRecordClinicalSection;
  fieldMeta: Record<
    ClinicianWorkbenchPatientRecordFieldPath,
    ClinicianWorkbenchPatientRecordFieldMeta
  >;
}

export type ClinicianWorkbenchChartPackDestination =
  | { kind: "form_field"; documentId: string; fieldPath: string }
  | { kind: "aggregate"; target: "reviewOfSystems" | "clinicalSignificance" }
  | { kind: "history_form" }
  | { kind: "operational_only"; reason: string };

export type ClinicianWorkbenchQuestionnaireTransform =
  | "yesNo"
  | "date"
  | "list"
  | "text"
  | "yesNoToBoolean"
  | "lbToKg"
  | "dateNormalize"
  | "toNumber"
  | "phoneNormalize"
  | "verbatim";

export interface ClinicianWorkbenchQuestionnaireFieldBinding {
  questionKey: string;
  // Accepts paths beyond the current ClinicianWorkbenchPatientRecordFieldPath
  // union: bindings are authored alongside record-model extensions, so a
  // binding may name a path the union gains in the same template version.
  fieldPath: string;
  transform?: ClinicianWorkbenchQuestionnaireTransform | null;
  // Every question must land somewhere printable or be explicitly
  // operational-only — the chart pack's no-orphan-answers rule. Optional at
  // the type level only for the legacy hand-authored overlay table;
  // generated/DB-served binding tables always carry it and
  // validateQuestionnaireFieldBindingV1 enforces its presence.
  chartPackDestination?: ClinicianWorkbenchChartPackDestination;
}

export function validateQuestionnaireFieldBindingV1(
  binding: unknown,
): string[] {
  const issues: string[] = [];
  const raw = (binding ?? {}) as Partial<ClinicianWorkbenchQuestionnaireFieldBinding>;

  if (typeof raw.questionKey !== "string" || raw.questionKey.length === 0) {
    issues.push("questionKey must be a non-empty string");
  }

  if (typeof raw.fieldPath !== "string" || raw.fieldPath.length === 0) {
    issues.push("fieldPath must be a non-empty string");
  }

  const destination = raw.chartPackDestination as
    | ClinicianWorkbenchChartPackDestination
    | null
    | undefined;

  if (!destination || typeof destination !== "object") {
    issues.push("chartPackDestination is required (no-orphan-answers rule)");
  } else {
    switch (destination.kind) {
      case "form_field":
        if (!destination.documentId) {
          issues.push("form_field destination requires documentId");
        }
        if (!destination.fieldPath) {
          issues.push("form_field destination requires fieldPath");
        }
        break;
      case "aggregate":
        if (
          destination.target !== "reviewOfSystems" &&
          destination.target !== "clinicalSignificance"
        ) {
          issues.push("aggregate destination requires a known target");
        }
        break;
      case "history_form":
        break;
      case "operational_only":
        if (!destination.reason) {
          issues.push("operational_only destination requires a reason");
        }
        break;
      default:
        issues.push("chartPackDestination.kind is not recognized");
    }
  }

  return issues;
}

export interface ClinicianWorkbenchSelectedPatient {
  patientId: string;
  displayName: string;
  identifier: string;
  summaryText: string;
  statusLabel: string;
  derivedFlagKeys: ClinicianWorkbenchDerivedFlagKey[];
  inMyPatients: boolean;
  canAddToMyPatients: boolean;
  coordinatorLabel: string;
  programLabel: string;
  nextAppointmentText: string;
  materialChangeToken: string;
  conversation: ClinicianWorkbenchConversation;
  taskAssignments: ClinicianWorkbenchTaskAssignmentSummary[];
  approvalSummaries: ClinicianWorkbenchApprovalSummary[];
  deliverySummaries: ClinicianWorkbenchDeliverySummary[];
  documentSummaries: ClinicianWorkbenchDocumentSummary[];
  voiceCallSummaries: ClinicianWorkbenchVoiceCallSummary[];
  detailSections: ClinicianWorkbenchDetailSection[];
  clinicalNotes: ClinicianWorkbenchClinicalNote[];
  record?: ClinicianWorkbenchPatientRecordV1 | null;
  /**
   * Count of red-flag questionnaire answers awaiting clinician review.
   * Optional until the hosted projections track review state.
   */
  pendingRedFlagCount?: number;
}

export interface ClinicianWorkbenchListSummary {
  myPatientsCount: number;
  allPatientsCount: number;
  attentionPatientCount: number;
  visiblePatientCount: number;
}

export interface ClinicianWorkbenchResponseV1 {
  schemaVersion: ClinicianWorkbenchContractVersion;
  tenantId: string;
  siteId: string | null;
  clinicianUserId: string;
  projectionVersion: string;
  generatedAt: string;
  routeState: {
    listScope: ClinicianWorkbenchListScope;
    attentionOnly: boolean;
    searchQuery: string | null;
    selectedPatientId: string | null;
    detailTab: ClinicianWorkbenchDetailTab;
  };
  metrics: ClinicianWorkbenchMetricGroup[];
  listSummary: ClinicianWorkbenchListSummary;
  patientList: ClinicianWorkbenchPatientListItem[];
  selectedPatient: ClinicianWorkbenchSelectedPatient | null;
}

export interface ClinicianWorkbenchAddPatientToMyListActionInput {
  kind: "add-patient-to-my-list";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
}

export interface ClinicianWorkbenchCompleteTaskAssignmentActionInput {
  kind: "complete-task-assignment";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  assignmentId: string;
}

export interface ClinicianWorkbenchMarkDocumentReviewedActionInput {
  kind: "mark-document-reviewed";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  documentId: string;
}

export interface ClinicianWorkbenchSendReplyActionInput {
  kind: "send-reply";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  bodyText: string;
}

export interface ClinicianWorkbenchApproveMessageReviewActionInput {
  kind: "approve-message-review";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  messageId: string;
  editedBodyText?: string | null;
}

export interface ClinicianWorkbenchRejectMessageReviewActionInput {
  kind: "reject-message-review";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  messageId: string;
}

export interface ClinicianWorkbenchRefreshCaseSummaryActionInput {
  kind: "refresh-case-summary";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  force?: boolean | null;
}

export interface ClinicianWorkbenchClearPatientAttentionActionInput {
  kind: "clear-patient-attention";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
}

export interface ClinicianWorkbenchUpsertClinicalRecordActionInput {
  kind: "upsert-clinical-record";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  targetKind: ClinicianWorkbenchClinicalRecordKind;
  recordId?: string | null;
  title: string;
  detailText: string;
}

export interface ClinicianWorkbenchArchiveClinicalRecordActionInput {
  kind: "archive-clinical-record";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  targetKind: ClinicianWorkbenchClinicalRecordKind;
  recordId: string;
  reasonText?: string | null;
}

export interface ClinicianWorkbenchReactivateClinicalRecordActionInput {
  kind: "reactivate-clinical-record";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  targetKind: ClinicianWorkbenchClinicalRecordKind;
  recordId: string;
}

export interface ClinicianWorkbenchUpsertClinicalNoteActionInput {
  kind: "upsert-clinical-note";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  noteId?: string | null;
  targetKind: ClinicianWorkbenchClinicalNoteTargetKind;
  targetId?: string | null;
  title: string;
  bodyText: string;
  visibility: ClinicianWorkbenchClinicalNoteVisibilityState;
}

export interface ClinicianWorkbenchArchiveClinicalNoteActionInput {
  kind: "archive-clinical-note";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  noteId: string;
  reasonText?: string | null;
}

export interface ClinicianWorkbenchReactivateClinicalNoteActionInput {
  kind: "reactivate-clinical-note";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  noteId: string;
}

export interface ClinicianWorkbenchMarkRedFlagsReviewedActionInput {
  kind: "mark-red-flags-reviewed";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  /** Omitted or null clears all of the patient's calls. */
  callTargetId?: string | null;
}

export interface ClinicianWorkbenchUpsertPatientRecordActionInput {
  kind: "upsert-patient-record";
  patientId: string;
  expectedMaterialChangeToken?: string | null;
  record: Partial<Omit<ClinicianWorkbenchPatientRecordV1, "fieldMeta">>;
  touchedFieldPaths: ClinicianWorkbenchPatientRecordFieldPath[];
  reviewConfirmations?: ClinicianWorkbenchPatientRecordFieldPath[] | null;
  /** Which kind of writer produced this upsert, stamped onto fieldMeta.source.
   *  Omitted means "manual" — a profile-form edit. The questionnaire declares
   *  itself so a later questionnaire save may clear its own earlier writes
   *  without being able to clear an HST import or a profile edit. */
  writerSource?: ClinicianWorkbenchPatientRecordSource | null;
}

export type ClinicianWorkbenchActionInput =
  | ClinicianWorkbenchAddPatientToMyListActionInput
  | ClinicianWorkbenchCompleteTaskAssignmentActionInput
  | ClinicianWorkbenchMarkDocumentReviewedActionInput
  | ClinicianWorkbenchSendReplyActionInput
  | ClinicianWorkbenchApproveMessageReviewActionInput
  | ClinicianWorkbenchRejectMessageReviewActionInput
  | ClinicianWorkbenchRefreshCaseSummaryActionInput
  | ClinicianWorkbenchClearPatientAttentionActionInput
  | ClinicianWorkbenchUpsertClinicalRecordActionInput
  | ClinicianWorkbenchArchiveClinicalRecordActionInput
  | ClinicianWorkbenchReactivateClinicalRecordActionInput
  | ClinicianWorkbenchUpsertClinicalNoteActionInput
  | ClinicianWorkbenchArchiveClinicalNoteActionInput
  | ClinicianWorkbenchReactivateClinicalNoteActionInput
  | ClinicianWorkbenchUpsertPatientRecordActionInput
  | ClinicianWorkbenchMarkRedFlagsReviewedActionInput;

export interface ClinicianWorkbenchActionRequestV1 {
  schemaVersion: ClinicianWorkbenchActionContractVersion;
  workbenchRequest: ClinicianWorkbenchRequestV1;
  action: ClinicianWorkbenchActionInput;
}

export interface ClinicianWorkbenchActionReceipt {
  actionId: string;
  kind: ClinicianWorkbenchActionKind;
  status: ClinicianWorkbenchActionStatus;
  patientId: string;
  messageId: string | null;
  appliedAt: string;
  summaryText: string;
}

export interface ClinicianWorkbenchActionResponseV1 {
  schemaVersion: ClinicianWorkbenchActionContractVersion;
  receipt: ClinicianWorkbenchActionReceipt;
  workbench: ClinicianWorkbenchResponseV1;
  changedScopes?: WorkspaceChangedScopeV1[];
}

export function resolveClinicianWorkbenchListScope(
  value: string | null | undefined,
): ClinicianWorkbenchListScope {
  return clinicianWorkbenchListScopes.includes(
    value as ClinicianWorkbenchListScope,
  )
    ? (value as ClinicianWorkbenchListScope)
    : "my";
}

export function resolveClinicianWorkbenchDetailTab(
  value: string | null | undefined,
): ClinicianWorkbenchDetailTab {
  return clinicianWorkbenchDetailTabs.includes(
    value as ClinicianWorkbenchDetailTab,
  )
    ? (value as ClinicianWorkbenchDetailTab)
    : "information";
}

export function resolveClinicianWorkbenchAttentionOnly(
  value: string | null | undefined,
): boolean {
  return value === "true";
}

export function resolveClinicianWorkbenchSearchQuery(
  value: string | null | undefined,
): string | null {
  const normalizedValue = value?.trim() ?? "";

  return normalizedValue.length > 0 ? normalizedValue : null;
}

export function buildClinicianWorkbenchRequestFromHttpQuery(
  query: ClinicianWorkbenchHttpQuery,
): ClinicianWorkbenchRequestV1 {
  return {
    schemaVersion: clinicianWorkbenchContractVersion,
    tenantId: query.tenantId,
    siteId: query.siteId ?? null,
    clinicianUserId: query.clinicianUserId,
    listScope: resolveClinicianWorkbenchListScope(query.listScope),
    attentionOnly: resolveClinicianWorkbenchAttentionOnly(query.attentionOnly),
    searchQuery: resolveClinicianWorkbenchSearchQuery(query.searchQuery),
    selectedPatientId: query.selectedPatientId ?? null,
    detailTab: resolveClinicianWorkbenchDetailTab(query.detailTab),
  };
}

export function buildClinicianWorkbenchHttpQuery(
  request: ClinicianWorkbenchRequestV1,
): ClinicianWorkbenchHttpQuery {
  return {
    tenantId: request.tenantId,
    siteId: request.siteId,
    clinicianUserId: request.clinicianUserId,
    listScope: request.listScope,
    attentionOnly: String(request.attentionOnly),
    searchQuery: request.searchQuery,
    selectedPatientId: request.selectedPatientId,
    detailTab: request.detailTab,
  };
}

export function buildClinicianWorkbenchActionRequest(
  workbenchRequest: ClinicianWorkbenchRequestV1,
  action: ClinicianWorkbenchActionInput,
): ClinicianWorkbenchActionRequestV1 {
  return {
    schemaVersion: clinicianWorkbenchActionContractVersion,
    workbenchRequest: {
      ...workbenchRequest,
      selectedPatientId: action.patientId,
    },
    action,
  };
}
